/**
 * Configuration Validation Utilities
 *
 * Validates merchant configuration structure to ensure:
 * - Required fields are present
 * - Data types are correct
 * - Bilingual text is properly formatted
 * - Action types are valid
 * - Logic constraints are met
 */

const VALID_SECTION_TYPES = ['photo_upload', 'question_set', 'completion'];
const VALID_QUESTION_TYPES = ['radio', 'checkbox', 'text', 'textarea'];
const VALID_ACTIONS = ['proceed', 'block', 'require_details'];
const VALID_PHOTO_MODES = ['optional', 'required', 'required_if_issues'];
const VALID_PAGINATION_MODES = ['one_per_page', 'accordion', 'all_at_once'];
const VALID_ITEM_STATUS = ['ok', 'issue', 'damaged', 'missing'];
const VALID_OVERALL_STATUS = ['all_ok', 'issues_reported'];

const ConfigValidator = {
  /**
   * Validate complete flow configuration
   * @param {Object} config - Complete merchant configuration
   * @returns {Array} - Array of error messages (empty if valid)
   */
  validateFlowConfig(config) {
    const errors = [];

    // Must have flow_config
    if (!config.flow_config) {
      errors.push('Missing flow_config');
      return errors;
    }

    // Must have sections array
    if (!config.flow_config.sections || !Array.isArray(config.flow_config.sections)) {
      errors.push('flow_config.sections must be an array');
      return errors;
    }

    // Must have at least completion section
    const hasCompletion = config.flow_config.sections.some(s => s.type === 'completion');
    if (!hasCompletion) {
      errors.push('Must have at least one "completion" section');
    }

    // Validate each section
    config.flow_config.sections.forEach((section, index) => {
      const sectionErrors = this.validateSection(section, index);
      errors.push(...sectionErrors);
    });

    // Check for duplicate section orders
    const orders = config.flow_config.sections.map(s => s.order);
    const hasDuplicateOrders = orders.length !== new Set(orders).size;
    if (hasDuplicateOrders) {
      errors.push('Section orders must be unique');
    }

    // Check for duplicate section IDs
    const ids = config.flow_config.sections.map(s => s.id);
    const hasDuplicateIds = ids.length !== new Set(ids).size;
    if (hasDuplicateIds) {
      errors.push('Section IDs must be unique');
    }

    return errors;
  },

  /**
   * Validate a single section
   */
  validateSection(section, index) {
    const errors = [];
    const prefix = `Section ${index} (${section.id || 'unnamed'})`;

    // Required fields
    if (!section.id) errors.push(`${prefix}: missing id`);
    if (!section.type) errors.push(`${prefix}: missing type`);
    if (section.order === undefined || section.order === null) {
      errors.push(`${prefix}: missing order`);
    }
    if (section.enabled === undefined) {
      errors.push(`${prefix}: missing enabled flag`);
    }

    // Validate type
    if (section.type && !VALID_SECTION_TYPES.includes(section.type)) {
      errors.push(`${prefix}: invalid type "${section.type}". Must be one of: ${VALID_SECTION_TYPES.join(', ')}`);
    }

    // Type-specific validation
    if (section.type === 'photo_upload') {
      errors.push(...this.validatePhotoSection(section, prefix));
    } else if (section.type === 'question_set') {
      errors.push(...this.validateQuestionSection(section, prefix));
    } else if (section.type === 'completion') {
      errors.push(...this.validateCompletionSection(section, prefix));
    }

    return errors;
  },

  /**
   * Validate photo upload section
   */
  validatePhotoSection(section, prefix) {
    const errors = [];
    const config = section.config || {};

    if (!section.config) {
      errors.push(`${prefix}: missing config`);
      return errors;
    }

    // Validate min/max photos
    if (config.min_photos !== undefined && config.min_photos < 0) {
      errors.push(`${prefix}: min_photos cannot be negative`);
    }
    if (config.max_photos !== undefined && config.max_photos < 0) {
      errors.push(`${prefix}: max_photos cannot be negative`);
    }
    if (config.min_photos !== undefined && config.max_photos !== undefined) {
      if (config.max_photos < config.min_photos) {
        errors.push(`${prefix}: max_photos must be >= min_photos`);
      }
    }
    if (config.max_photos > 50) {
      errors.push(`${prefix}: max_photos cannot exceed 50 (performance limit)`);
    }

    // Validate bilingual text
    if (config.header_text) {
      errors.push(...this.validateBilingualText(config.header_text, `${prefix}.header_text`));
    }
    if (config.instructions) {
      errors.push(...this.validateBilingualText(config.instructions, `${prefix}.instructions`));
    }

    // Validate scope
    if (config.scope && !['booking', 'item'].includes(config.scope)) {
      errors.push(`${prefix}: scope must be "booking" or "item"`);
    }

    return errors;
  },

  /**
   * Validate question set section
   */
  validateQuestionSection(section, prefix) {
    const errors = [];
    const config = section.config || {};

    if (!section.config) {
      errors.push(`${prefix}: missing config`);
      return errors;
    }

    // Validate scope
    if (!config.scope) {
      errors.push(`${prefix}: missing scope`);
    } else if (!['booking', 'item'].includes(config.scope)) {
      errors.push(`${prefix}: scope must be "booking" or "item"`);
    }

    // Validate questions array
    if (!Array.isArray(config.questions)) {
      errors.push(`${prefix}: config.questions must be an array`);
      return errors;
    }

    // Max questions limit
    const maxQuestions = config.scope === 'item' ? 5 : 5;
    if (config.questions.length > maxQuestions) {
      errors.push(`${prefix}: too many questions (max ${maxQuestions} for ${config.scope} scope)`);
    }

    // Validate each question
    config.questions.forEach((question, qIndex) => {
      const qPrefix = `${prefix}.questions[${qIndex}]`;
      errors.push(...this.validateQuestion(question, qPrefix));
    });

    // Item-level specific validation
    if (config.scope === 'item') {
      if (config.pagination && !VALID_PAGINATION_MODES.includes(config.pagination)) {
        errors.push(`${prefix}: invalid pagination mode "${config.pagination}". Must be one of: ${VALID_PAGINATION_MODES.join(', ')}`);
      }

      if (config.item_photos_config) {
        const photoMode = config.item_photos_config.mode;
        if (photoMode && !VALID_PHOTO_MODES.includes(photoMode)) {
          errors.push(`${prefix}: invalid item_photos_config.mode "${photoMode}". Must be one of: ${VALID_PHOTO_MODES.join(', ')}`);
        }

        if (config.item_photos_config.min_photos !== undefined && config.item_photos_config.min_photos < 0) {
          errors.push(`${prefix}.item_photos_config: min_photos cannot be negative`);
        }
        if (config.item_photos_config.max_photos !== undefined && config.item_photos_config.max_photos < 0) {
          errors.push(`${prefix}.item_photos_config: max_photos cannot be negative`);
        }
      }
    }

    // Validate bilingual text
    if (config.header_text) {
      errors.push(...this.validateBilingualText(config.header_text, `${prefix}.header_text`));
    }
    if (config.instructions) {
      errors.push(...this.validateBilingualText(config.instructions, `${prefix}.instructions`));
    }

    return errors;
  },

  /**
   * Validate a single question
   */
  validateQuestion(question, prefix) {
    const errors = [];

    // Required fields
    if (!question.id) errors.push(`${prefix}: missing id`);
    if (!question.text) errors.push(`${prefix}: missing text`);
    if (!question.type) errors.push(`${prefix}: missing type`);
    if (question.order === undefined) errors.push(`${prefix}: missing order`);

    // Validate question type
    if (question.type && !VALID_QUESTION_TYPES.includes(question.type)) {
      errors.push(`${prefix}: invalid type "${question.type}". Must be one of: ${VALID_QUESTION_TYPES.join(', ')}`);
    }

    // Validate bilingual text
    if (question.text) {
      errors.push(...this.validateBilingualText(question.text, `${prefix}.text`));
    }
    if (question.preamble) {
      errors.push(...this.validateBilingualText(question.preamble, `${prefix}.preamble`));
    }

    // Validate options (for radio/checkbox types)
    if (['radio', 'checkbox'].includes(question.type)) {
      if (!Array.isArray(question.options) || question.options.length === 0) {
        errors.push(`${prefix}: must have at least one option for type "${question.type}"`);
        return errors;
      }

      // Validate each option
      question.options.forEach((option, oIndex) => {
        const oPrefix = `${prefix}.options[${oIndex}]`;
        errors.push(...this.validateOption(option, oPrefix));
      });

      // Check for duplicate option values
      const values = question.options.map(o => o.value);
      const hasDuplicates = values.length !== new Set(values).size;
      if (hasDuplicates) {
        errors.push(`${prefix}: option values must be unique`);
      }
    }

    return errors;
  },

  /**
   * Validate a question option
   */
  validateOption(option, prefix) {
    const errors = [];

    // Required fields
    if (!option.value) errors.push(`${prefix}: missing value`);
    if (!option.label) errors.push(`${prefix}: missing label`);
    if (!option.action) errors.push(`${prefix}: missing action`);

    // Validate action type
    if (option.action && !VALID_ACTIONS.includes(option.action)) {
      errors.push(`${prefix}: invalid action "${option.action}". Must be one of: ${VALID_ACTIONS.join(', ')}`);
    }

    // Validate bilingual label
    if (option.label) {
      errors.push(...this.validateBilingualText(option.label, `${prefix}.label`));
    }

    // Action-specific validation
    if (option.action === 'block') {
      if (!option.blocking_message) {
        errors.push(`${prefix}: action "block" requires blocking_message`);
      } else {
        errors.push(...this.validateBilingualText(option.blocking_message, `${prefix}.blocking_message`));
      }
    }

    if (option.action === 'require_details') {
      if (!option.details_config) {
        errors.push(`${prefix}: action "require_details" requires details_config`);
      } else {
        if (!option.details_config.field_name) {
          errors.push(`${prefix}.details_config: missing field_name`);
        }
        if (option.details_config.placeholder) {
          errors.push(...this.validateBilingualText(option.details_config.placeholder, `${prefix}.details_config.placeholder`));
        }
      }
    }

    return errors;
  },

  /**
   * Validate completion section
   */
  validateCompletionSection(section, prefix) {
    const errors = [];
    const config = section.config || {};

    if (!section.config) {
      errors.push(`${prefix}: missing config`);
      return errors;
    }

    // Validate bilingual text
    if (config.header_text) {
      errors.push(...this.validateBilingualText(config.header_text, `${prefix}.header_text`));
    }
    if (config.message) {
      errors.push(...this.validateBilingualText(config.message, `${prefix}.message`));
    }

    // Validate review request
    if (config.review_request) {
      if (config.review_request.text) {
        errors.push(...this.validateBilingualText(config.review_request.text, `${prefix}.review_request.text`));
      }
      if (config.review_request.button_text) {
        errors.push(...this.validateBilingualText(config.review_request.button_text, `${prefix}.review_request.button_text`));
      }
    }

    // Validate instruction boxes
    if (config.instruction_boxes) {
      if (!Array.isArray(config.instruction_boxes)) {
        errors.push(`${prefix}: instruction_boxes must be an array`);
      } else {
        if (config.instruction_boxes.length > 3) {
          errors.push(`${prefix}: maximum 3 instruction boxes allowed`);
        }

        config.instruction_boxes.forEach((box, bIndex) => {
          const bPrefix = `${prefix}.instruction_boxes[${bIndex}]`;

          if (box.title) {
            errors.push(...this.validateBilingualText(box.title, `${bPrefix}.title`));
          }
          if (box.text) {
            errors.push(...this.validateBilingualText(box.text, `${bPrefix}.text`));
          }
          if (box.order === undefined) {
            errors.push(`${bPrefix}: missing order`);
          }
        });
      }
    }

    return errors;
  },

  /**
   * Validate bilingual text (must have 'de' and 'en' keys)
   */
  validateBilingualText(text, prefix) {
    const errors = [];

    if (typeof text !== 'object' || text === null) {
      errors.push(`${prefix}: must be an object with de/en keys`);
      return errors;
    }

    if (!text.de || typeof text.de !== 'string' || text.de.trim() === '') {
      errors.push(`${prefix}: missing or invalid "de" text`);
    }
    if (!text.en || typeof text.en !== 'string' || text.en.trim() === '') {
      errors.push(`${prefix}: missing or invalid "en" text`);
    }

    return errors;
  },

  /**
   * Validate item status value
   */
  validateItemStatus(status) {
    if (!VALID_ITEM_STATUS.includes(status)) {
      return `Invalid item_status "${status}". Must be one of: ${VALID_ITEM_STATUS.join(', ')}`;
    }
    return null;
  },

  /**
   * Validate overall status value
   */
  validateOverallStatus(status) {
    if (!VALID_OVERALL_STATUS.includes(status)) {
      return `Invalid overall_status "${status}". Must be one of: ${VALID_OVERALL_STATUS.join(', ')}`;
    }
    return null;
  }
};

export default ConfigValidator;
